import { useEffect } from "react";

/**
 * Attaches a `beforeunload` listener while `isDirty` is true so the browser
 * shows its native "you have unsaved changes" warning when the user closes
 * the tab, navigates away, or refreshes.
 *
 * The listener auto-detaches when:
 * - the hosting component unmounts
 * - `isDirty` transitions back to false (e.g. after a successful submission)
 *
 * Modern browsers ignore custom messages and always render their own copy.
 * We still set `returnValue = ""` because some legacy browsers require a
 * truthy assignment to opt into the prompt.
 */
export function useUnsavedChangesGuard(isDirty: boolean): void {
  useEffect(() => {
    if (!isDirty) return;

    function handleBeforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault();
      event.returnValue = "";
    }

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty]);
}
